import { inject, Injectable } from '@angular/core';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { combineLatest, map, Observable, of, switchMap, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { LoginResponse, User, UserPermission } from '@/api/models';
import { AuthService as ApiAuthService } from '@/api/services/auth.service';
import { UserService as ApiUserService } from '@/api/services/user.service';
import { environment } from '@/config/environments/environment';
import { Role } from '@/core/types/role';
import { getStorageObject, removeStorageObject } from '@/core/utils/storage-manager';
import { EditTeacherAvailabilityConfirmationComponent } from '@/features/teachers/components/edit-teacher-availability/edit-teacher-availability-confirmation.component';
import { EducaDialog } from '@/shared/components/dialog';

import { FeatureFlagService } from './roles.service';
import { ScopeService } from './scope.service';
import { UserService } from './user.service';

/**
 * Interface that represents the user credentials
 */
export interface CredentialsInterface {
  email: string;
  password: string;
  remember?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private userService = inject(UserService);
  private scopeService = inject(ScopeService);
  private apiUserService = inject(ApiUserService);
  private apiAuthService = inject(ApiAuthService);
  private featureFlagService = inject(FeatureFlagService);
  private queryClient = inject(QueryClient);
  readonly dialog = inject(EducaDialog);
  protected readonly role = Role;

  constructor() {
    if (getStorageObject(environment.userDataStorage)) {
      this.fetchSessionIdentity().subscribe();
    }
  }

  /**
   * Url to redirect after login
   */
  redirectUrl: string | null = null;

  login(loginCredentials: CredentialsInterface): Observable<LoginResponse> {
    return this.apiAuthService
      .apiAuthCookiePost({
        body: loginCredentials
      })
      .pipe(switchMap((loginResponse) => this.fetchSessionIdentity().pipe(map(() => loginResponse))));
  }

  validateSession(): Observable<boolean> {
    return this.apiAuthService
      .apiAuthCookieValidateGet({
        withCredentials: true
      })
      .pipe(
        // on success, return true
        map(() => true),
        catchError(async () => {
          return false;
        })
      );
  }

  getSessionIdentity(): Observable<User> {
    const user = getStorageObject(environment.userDataStorage);
    if (!user) {
      throw new Error('User storage object not found');
    }

    return of(user);
  }

  fetchSessionIdentity(): Observable<User & { permissions: { ticket_scopes: UserPermission['ticket_scopes'] } }> {
    return combineLatest([
      this.apiAuthService.apiAuthCookieIdentityGet(),
      this.apiUserService.apiUserPermissionsGet(),
      this.apiUserService.apiUserAvailabilityGet()
    ]).pipe(
      tap(([user, permissions, availability]) => {
        this.userService.setUserData(user, permissions, availability.is_available);
        this.featureFlagService.updateRole(user);
        this.scopeService.setScopes(permissions.ticket_scopes.map((scope) => scope.scope));
        if (!this.userService.isAvailable() && user.role !== Role.READ_ONLY) {
          this.dialog.open(EditTeacherAvailabilityConfirmationComponent);
        }
      }),
      map(([user, permissions]) => ({ ...user, permissions: { ticket_scopes: permissions.ticket_scopes } }))
    );
  }
  /**
   * Remove the token from local storage and redirect the user to login page
   */
  logout(): Observable<void> {
    return this.apiAuthService.apiAuthCookieDelete().pipe(
      map(() => {
        this.clearSession();
      })
    );
  }

  clearSession(): void {
    removeStorageObject(environment.userDataStorage);
    this.featureFlagService.clearRole();
    this.queryClient.clear();
    this.scopeService.setScopes([]);
  }
}
